import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import EventDetail from "@/pages/EventDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import MyRegistrations from "@/pages/MyRegistrations";
import GPXViewer from "@/pages/GPXViewer";
import RouteTracker from "@/pages/RouteTracker";
import BeaconMode from "@/pages/BeaconMode";
import PassportMap from "@/pages/PassportMap";
import RallyMasterDashboard from "@/pages/RallyMasterDashboard";
import LiveMapView from "@/pages/LiveMapView";
import Profile from "@/pages/Profile";
import FollowTrack from "@/pages/FollowTrack";
import LegalNotice from "@/pages/LegalNotice";
import TermsOfUse from "@/pages/TermsOfUse";
import PrivacyAndCookies from "@/pages/PrivacyAndCookies";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  console.error('REACT_APP_BACKEND_URL is not defined!');
}
console.log('Using BACKEND_URL:', BACKEND_URL);
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRallyMaster, setIsRallyMaster] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      checkRallyMasterStatus(token);
    }
    setLoading(false);
  }, []);

  const checkRallyMasterStatus = async (token) => {
    try {
      const response = await axios.get(`${API}/user/is-rally-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsRallyMaster(response.data.is_rally_master);
    } catch (error) {
      console.error('Error checking rally master status:', error);
      setIsRallyMaster(false);
    }
  };

  const login = async (userData, token) => {
    localStorage.setItem('token', token);
    
    // Fetch complete user data from backend
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const completeUserData = response.data;
      localStorage.setItem('user', JSON.stringify(completeUserData));
      setUser(completeUserData);
      checkRallyMasterStatus(token);
    } catch (error) {
      // Fallback to userData from token if fetch fails
      console.error('Error fetching complete user data:', error);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Sesión cerrada');
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        {user && <Navigation user={user} logout={logout} isRallyMaster={isRallyMaster} />}
        <div className={`min-h-screen ${user ? 'pb-28 md:pb-0' : ''}`}>
          <Routes>
            <Route path="/login" element={!user ? <Login onLogin={login} /> : <Navigate to="/" />} />
            <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={user ? <Home user={user} /> : <Navigate to="/login" />} />
            <Route path="/events/:id" element={user ? <EventDetail user={user} /> : <Navigate to="/login" />} />
            <Route path="/admin" element={user?.is_admin ? <AdminDashboard user={user} /> : <Navigate to="/" />} />
            <Route path="/rally-master" element={(user?.is_admin || isRallyMaster) ? <RallyMasterDashboard user={user} /> : <Navigate to="/" />} />
            <Route path="/rally-master/live-map/:eventId" element={(user?.is_admin || isRallyMaster) ? <LiveMapView user={user} /> : <Navigate to="/" />} />
            <Route path="/my-registrations" element={user ? <MyRegistrations user={user} /> : <Navigate to="/login" />} />
            <Route path="/profile" element={user ? <Profile user={user} onUserUpdate={updateUser} /> : <Navigate to="/login" />} />
            <Route path="/follow-track/:trackId" element={user ? <FollowTrack user={user} /> : <Navigate to="/login" />} />
            <Route path="/gpx/:eventId" element={user ? <GPXViewer user={user} /> : <Navigate to="/login" />} />
            <Route path="/route-tracker/:registrationId" element={user ? <RouteTracker user={user} /> : <Navigate to="/login" />} />
            <Route path="/beacon/:registrationId" element={user ? <BeaconMode /> : <Navigate to="/login" />} />
            <Route path="/pasaporte/:registrationId" element={user ? <PassportMap /> : <Navigate to="/login" />} />
            <Route path="/aviso-legal" element={<LegalNotice />} />
            <Route path="/condiciones-de-uso" element={<TermsOfUse />} />
            <Route path="/privacidad-y-cookies" element={<PrivacyAndCookies />} />
          </Routes>
        </div>
        <Footer />
      </BrowserRouter>
      <CookieBanner />
      <InstallPWAPrompt />
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
